import { NETWORK_RECONNECT_TIMEOUT_MS } from './constants';
import { callT } from '~/utils/callI18n';

export const logJanus = (_tag: string, ..._args: unknown[]): void => {};

export function isMicAccessError(err: unknown): boolean {
  if (
    typeof DOMException !== 'undefined' &&
    err instanceof DOMException &&
    (err.name === 'NotFoundError' ||
      err.name === 'NotReadableError' ||
      err.name === 'AbortError' ||
      err.name === 'NotAllowedError' ||
      err.name === 'SecurityError')
  ) {
    return true;
  }
  if (err instanceof Error) {
    return /microphone|micro|NotFoundError|not found|Requested device not found|Permission denied|Permission dismissed|NotAllowedError/i.test(
      err.message
    );
  }
  return false;
}

export function hasLiveLocalMic(r: { localStreamRef: { current: MediaStream | null } }): boolean {
  return (r.localStreamRef.current?.getAudioTracks() ?? []).some((t) => t.readyState === 'live');
}

export function txId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export function parseRoomId(room: string | number): string {
  const id = String(room).trim();
  if (!id) {
    throw new Error(callT('errors.invalidRoomId'));
  }
  return id;
}

export function parsePublisherFeedRef(value: unknown): number | string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s || s === 'ok') return undefined;
    return s;
  }
  return undefined;
}

export function waitForNetwork(maxMs = NETWORK_RECONNECT_TIMEOUT_MS): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('online', onOnline);
      clearTimeout(timerId);
      fn();
    };

    const onOnline = () => finish(resolve);
    const timerId = setTimeout(
      () => finish(() => reject(new Error(`waitForNetwork timeout after ${maxMs}ms`))),
      maxMs
    );

    window.addEventListener('online', onOnline);
  });
}

export function isPublisherIceConnected(r: import('./sessionRefs').JanusCallRefs): boolean {
  const ice = r.publisherPcRef.current?.iceConnectionState;
  return ice === 'connected' || ice === 'completed';
}

export function finishJoinIfPublisherReady(
  r: import('./sessionRefs').JanusCallRefs,
  set: import('./sessionRefs').JanusCallSetters
): void {
  if (!r.isPublisherJoinedRef.current || !r.isPublisherConfiguredRef.current) {
    return;
  }
  if (!r.isJoinedRef.current) {
    r.isJoinedRef.current = true;
    set.setCallStatus('oncall');
    r.joinRoomResolveRef.current?.();
    r.joinRoomResolveRef.current = null;
    r.joinRoomRejectRef.current = null;
    if (!isPublisherIceConnected(r)) {
      logJanus('PUB', 'oncall before publisher ICE connected — audio may follow', {
        ice: r.publisherPcRef.current?.iceConnectionState,
      });
    }
  }
}

export async function drainPendingCandidates(
  pc: RTCPeerConnection,
  pending: RTCIceCandidate[]
): Promise<void> {
  while (pending.length > 0) {
    const c = pending.shift();
    if (c) {
      await pc.addIceCandidate(c).catch(() => undefined);
    }
  }
}
